import React from 'react';
import { useTranslation } from 'react-i18next';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const BandFilters = ({ 
  filters, 
  onFilterChange, 
  onClearFilters, 
  onCreateBand,
  searchTerm,
  onSearchChange 
}) => {
  const { t } = useTranslation();
  const departmentOptions = [
    { value: '', label: 'All Departments' },
    { value: 'engineering', label: 'Engineering' },
    { value: 'sales', label: 'Sales' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'hr', label: 'Human Resources' },
    { value: 'finance', label: 'Finance' },
    { value: 'operations', label: 'Operations' },
    { value: 'legal', label: 'Legal' },
    { value: 'customer-success', label: 'Customer Success' }
  ];

  const jobLevelOptions = [
    { value: '', label: 'All Levels' },
    { value: 'entry', label: 'Entry Level' },
    { value: 'junior', label: 'Junior' },
    { value: 'mid', label: 'Mid Level' },
    { value: 'senior', label: 'Senior' },
    { value: 'lead', label: 'Lead' },
    { value: 'principal', label: 'Principal' },
    { value: 'director', label: 'Director' },
    { value: 'vp', label: 'Vice President' },
    { value: 'c-level', label: 'C-Level' }
  ];

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'active', label: 'Active' },
    { value: 'draft', label: 'Draft' },
    { value: 'pending', label: 'Pending Review' },
    { value: 'archived', label: 'Archived' }
  ];

  const currencyOptions = [
    { value: '', label: 'All Currencies' },
    { value: 'EUR', label: 'EUR (Euro)' },
    { value: 'USD', label: 'USD (US Dollar)' },
    { value: 'GBP', label: 'GBP (British Pound)' },
    { value: 'SEK', label: 'SEK (Swedish Krona)' },
    { value: 'DKK', label: 'DKK (Danish Krone)' },
    { value: 'PLN', label: 'PLN (Polish Zloty)' }
  ];

  const hasActiveFilters = Object.values(filters)?.some(value => value !== '') || searchTerm !== '';

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Pay Band Management</h2>
          <p className="text-sm text-muted-foreground">Create and manage salary band structures with compliance tracking</p>
        </div>
        <Button 
          variant="default"
          iconName="Plus"
          iconPosition="left"
          onClick={onCreateBand}
        >
          Create New Band
        </Button>
      </div>
      {/* Search Bar */}
      <div className="mb-4">
        <Input
          type="search"
          placeholder={t('payBandsPage.filters.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => onSearchChange(e?.target?.value)}
          className="max-w-md"
        />
      </div>
      {/* Filter Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Select
          label="Department"
          options={departmentOptions}
          value={filters?.department}
          onChange={(value) => onFilterChange('department', value)}
          placeholder={t('payBandsPage.filters.selectDepartment')}
        />

        <Select
          label={t('payBandsPage.filters.jobLevel')}
          options={jobLevelOptions}
          value={filters?.jobLevel}
          onChange={(value) => onFilterChange('jobLevel', value)}
          placeholder={t('payBandsPage.filters.selectJobLevel')}
        />

        <Select
          label="Status"
          options={statusOptions}
          value={filters?.status}
          onChange={(value) => onFilterChange('status', value)}
          placeholder={t('payBandsPage.filters.selectStatus')}
        />

        <Select
          label="Currency"
          options={currencyOptions}
          value={filters?.currency}
          onChange={(value) => onFilterChange('currency', value)}
          placeholder={t('payBandsPage.filters.selectCurrency')}
        />
      </div>
      {/* Clear Filters */}
      {hasActiveFilters && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
          <span className="text-sm text-muted-foreground">
            {Object.values(filters)?.filter(v => v !== '')?.length + (searchTerm ? 1 : 0)} filter(s) applied
          </span>
          <Button 
            variant="ghost" 
            size="sm"
            iconName="X"
            iconPosition="left"
            onClick={onClearFilters}
          >
            Clear All Filters
          </Button>
        </div>
      )}
    </div>
  );
};

export default BandFilters;